/**
 * Platform billing: GET /networks/:id/infra-usage, GET /usage/infra (accountMode), or GET /orgs/:orgId/infra-usage (orgMode).
 * Storage gauges + infra rollup metrics. Sub-tabs: Vector DB (managed) / General / Buffer.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getRestApiBearerToken } from '@/services/apiToken';
import { Link } from 'react-router-dom';
import { Tab } from '@headlessui/react';
import { motion } from 'framer-motion';
import { Boxes, Cpu, Database, HardDrive } from 'lucide-react';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

/** Mirrors `stepflow_core.billing_gauges.BILLING_TAB_UI_HINTS` when API omits hints (older deploy). */
const DEFAULT_BILLING_TAB_HINTS = {
  vectorDb: {
    storageGaugeKey: 's3LanceBytesReported',
    rollupMetricPrefixes: ['vectorStream', 'memorySearch', 'memoryCold', 'memoryHot'],
  },
  generalPurpose: {
    storageGaugeKey: 's3AppDataBytesReported',
    rollupMetricPrefixes: [
      'restApi',
      'workflowSfn',
      'memoryManagerSfn',
      'brainSfn',
      'networkDeploymentSfn',
      'docProcessor',
      'workflowApproval',
    ],
  },
  buffer: {
    storageGaugeKey: 'dynamoBufferBytesReported',
    rollupMetricPrefixes: [],
  },
};

const TAB_CONFIG = [
  {
    id: 'vectorDb',
    label: 'Vector DB (managed)',
    tabTitle:
      'Managed vector index: stored embedding bytes, plus ingest/sync/search compute—capacity and ROI in one place.',
    hintKey: 'vectorDb',
    icon: Database,
    accent: 'from-violet-500 to-indigo-500',
  },
  {
    id: 'general',
    label: 'General',
    hintKey: 'generalPurpose',
    icon: HardDrive,
    accent: 'from-sky-500 to-blue-600',
  },
  { id: 'buffer', label: 'Buffer', hintKey: 'buffer', icon: Boxes, accent: 'from-teal-500 to-emerald-600' },
];

/** Subset of platform billing tabs (e.g. Usage splits Vector DB vs app/buffer). */
function resolveTabDefs(visibleTabIds) {
  if (!visibleTabIds?.length) return TAB_CONFIG;
  return visibleTabIds.map((id) => TAB_CONFIG.find((t) => t.id === id)).filter(Boolean);
}

export function formatUsd(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n));
}

export function formatBytes(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const b = Number(n);
  if (b === 0) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = b;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i += 1;
  }
  const rounded = i === 0 ? Math.round(v) : v >= 10 ? Math.round(v) : Math.round(v * 10) / 10;
  return `${rounded} ${u[i]}`;
}

/** S3 / vector / Dynamo buffer byte totals come from a ~daily scheduled snapshot—not live. */
export function StorageGaugeDailyBadge({ className = '' }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-900 ${className}`}
      title="Storage gauge rows refresh about once per day (scheduled job). They are point-in-time snapshots, not streaming byte meters."
    >
      Updates daily
    </span>
  );
}

/** Charts and summaries fetched when you open or refresh this view. */
export function RefreshesOnLoadBadge({ className = '' }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full border border-sky-200 bg-sky-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-sky-900 ${className}`}
      title="Data is loaded from the API when you open or click Refresh—not on the same schedule as daily storage snapshots."
    >
      Refreshes on load
    </span>
  );
}

function humanizeMetricKey(key) {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function formatMetricNumber(key, val) {
  const n = Number(val);
  if (val == null || Number.isNaN(n)) return '—';
  if (key.endsWith('Ms')) return `${n.toLocaleString(undefined, { maximumFractionDigits: 0 })} ms`;
  if (key.endsWith('Seconds') || key.endsWith('GbSeconds') || key.endsWith('VpcuSeconds')) {
    if (n === 0) return '0';
    return n >= 100 ? n.toLocaleString(undefined, { maximumFractionDigits: 0 }) : n.toFixed(3);
  }
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function sumMetricsByPrefixes(monthRows, prefixes) {
  const acc = {};
  if (!prefixes?.length) return acc;
  monthRows.forEach(({ metrics }) => {
    Object.entries(metrics || {}).forEach(([k, v]) => {
      if (prefixes.some((p) => k.startsWith(p))) {
        acc[k] = (acc[k] || 0) + Number(v);
      }
    });
  });
  return acc;
}

function sortedMetricEntries(obj) {
  return Object.entries(obj).sort(([a], [b]) => a.localeCompare(b));
}

/** Sum Lambda-style dimensions from infra rollups (keys are camelCase from metering). */
function summarizeRollupCompute(totalsObj) {
  const out = { vpcuSeconds: 0, gbSeconds: 0, durationMs: 0 };
  Object.entries(totalsObj || {}).forEach(([k, v]) => {
    const n = Number(v);
    if (Number.isNaN(n)) return;
    if (k.endsWith('VpcuSeconds')) out.vpcuSeconds += n;
    else if (k.endsWith('GbSeconds')) out.gbSeconds += n;
    else if (k.endsWith('DurationMs')) out.durationMs += n;
  });
  return out;
}

function formatDurationMsTotal(ms) {
  if (ms == null || Number.isNaN(ms) || ms <= 0) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  if (s < 3600) return `${s >= 100 ? Math.round(s) : s.toFixed(1)} s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
}

function storageHeroLabels(tabId) {
  if (tabId === 'vectorDb') {
    return {
      kicker: 'Vector DB (managed) · index capacity',
      sub:
        'Your embeddings live in a managed vector store we operate for you—no clusters to patch. This number is a point-in-time snapshot of index bytes (updated daily); use it for growth and chargeback.',
    };
  }
  if (tabId === 'general') {
    return {
      kicker: 'Application storage',
      sub: 'App-data S3 prefix footprint (point-in-time snapshot)',
    };
  }
  if (tabId === 'buffer') {
    return {
      kicker: 'Buffer footprint',
      sub: 'Estimated DynamoDB payload size for this network under your user partition',
    };
  }
  return { kicker: 'Storage', sub: 'Point-in-time snapshot from metering' };
}

export default function PlatformBillingPanel({
  network,
  accountMode = false,
  /** Cross-workspace payer rollup; requires ``orgId`` matching token ``oid`` / custom:org_id. */
  orgMode = false,
  orgId = '',
  months: monthsProp,
  onMonthsChange,
  /** e.g. ['vectorDb'] or ['general','buffer'] — omit for all tabs (default). Not used with accountMode/orgMode. */
  visibleTabIds,
  /** Hide top “Platform & storage” heading when a parent section already introduced this block. */
  suppressOuterTitle = false,
  /** When account summary shows platform charges elsewhere (e.g. account Billing tab). */
  suppressCustomerEstimates = false,
}) {
  const { user } = useAuth();
  const networkId = network?.networkId;
  const [internalMonths, setInternalMonths] = useState(6);
  const monthsControlled = monthsProp != null && typeof onMonthsChange === 'function';
  const months = monthsControlled ? monthsProp : internalMonths;
  const setMonths = monthsControlled ? onMonthsChange : setInternalMonths;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  const fetchInfra = useCallback(async () => {
    if (orgMode && !orgId) return;
    if (!accountMode && !orgMode && (!networkId || networkId === 'new')) return;
    setLoading(true);
    setError(null);
    try {
      const idToken = getRestApiBearerToken();
      const q = new URLSearchParams({ months: String(months) });
      let url;
      if (orgMode) {
        url = `${API_BASE_URL}/orgs/${encodeURIComponent(orgId)}/infra-usage?${q}`;
      } else if (accountMode) {
        url = `${API_BASE_URL}/usage/infra?${q}`;
      } else {
        url = `${API_BASE_URL}/networks/${networkId}/infra-usage?${q}`;
      }
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || body.message || response.statusText);
      }
      setPayload(body);
    } catch (e) {
      setError(e.message || 'Failed to load platform usage');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [accountMode, orgMode, orgId, networkId, user?.idToken, months]);

  useEffect(() => {
    fetchInfra();
  }, [fetchInfra]);

  const hints = payload?.billingTabHints || DEFAULT_BILLING_TAB_HINTS;
  const monthRows = payload?.months || [];
  const range = payload?.range;
  const byNetwork = payload?.byNetwork || [];
  const userGlobalTotals = payload?.userGlobalTotals;

  const hasAnyStorageGaugeInRange = useMemo(
    () => monthRows.some((r) => r.storageGauges?.billingGaugesCollectedAt),
    [monthRows]
  );

  const tabDefs = useMemo(
    () => resolveTabDefs(accountMode || orgMode ? undefined : visibleTabIds),
    [accountMode, orgMode, visibleTabIds]
  );

  const tabData = useMemo(() => {
    return tabDefs.map((tab) => {
      const h = hints[tab.hintKey] || {};
      const storageKey = h.storageGaugeKey;
      const prefixes = h.rollupMetricPrefixes || [];
      const computeTotals = sumMetricsByPrefixes(monthRows, prefixes);
      const computeSummary = summarizeRollupCompute(computeTotals);
      const storageByMonth = monthRows.map((row) => ({
        month: row.month,
        bytes: row.storageGauges?.[storageKey],
        collectedAt: row.storageGauges?.billingGaugesCollectedAt,
      }));
      const latestStorageRow = [...storageByMonth].reverse().find((r) => r.bytes != null);
      const hasGaugeRun = storageByMonth.some((r) => r.collectedAt);
      return {
        ...tab,
        storageKey,
        storageByMonth,
        latestBytes: latestStorageRow?.bytes,
        latestCollectedAt: latestStorageRow?.collectedAt,
        computeEntries: sortedMetricEntries(computeTotals),
        computeSummary,
        hasGaugeRun,
      };
    });
  }, [hints, monthRows, tabDefs]);

  const monthsFieldId = useMemo(() => {
    if (orgMode) return 'org-platform-usage-months';
    if (accountMode) return 'acct-platform-usage-months';
    if (visibleTabIds?.length) return `platform-infra-${visibleTabIds.join('-')}-months`;
    return 'platform-usage-months';
  }, [accountMode, orgMode, visibleTabIds]);

  if (orgMode && !orgId) {
    return null;
  }
  if (!accountMode && !orgMode && (!networkId || networkId === 'new')) {
    return null;
  }

  if (tabDefs.length === 0) {
    return (
      <div className="rounded-lg border border-amber-100 bg-amber-50/50 px-4 py-3 text-sm text-amber-900">
        No billing tabs to display — check the{' '}
        <code className="rounded bg-white/80 px-1 text-xs">visibleTabIds</code> prop.
      </div>
    );
  }

  const userGlobalEntries =
    userGlobalTotals && typeof userGlobalTotals === 'object'
      ? sortedMetricEntries(userGlobalTotals)
      : [];

  return (
    <div className="space-y-6">
      <div
        className={`flex flex-col gap-4 ${suppressOuterTitle ? 'sm:flex-row sm:justify-end' : 'sm:flex-row sm:items-end sm:justify-between'}`}
      >
        {!suppressOuterTitle && (
          <div>
            <div className="flex flex-wrap items-center gap-2 gap-y-1">
              <h3 className="text-base font-semibold text-gray-900">Platform &amp; storage</h3>
              <StorageGaugeDailyBadge />
            </div>
            <p className="text-sm text-gray-500 mt-1 max-w-2xl">
              {orgMode ? (
                <>
                  Organization-wide: all workspaces that share this payer org in metering. Storage per month sums
                  per-network gauge snapshots; compute rollups are additive. Historical rows without{' '}
                  <code className="text-xs">org_id</code> may be missing here until backfilled.
                </>
              ) : accountMode ? (
                <>
                  Aggregated across all your networks plus account-level infra (calls with no network context). Storage
                  per month is the sum of per-network gauge snapshots; compute rollups are additive over the range. First
                  storage numbers usually appear within about a day after deploy, while compute rollups can show up sooner.
                </>
              ) : (
                <>
                  Billable signals from metering: S3 and vector-index footprint, DynamoDB payload estimate, and infra compute
                  rollups. Byte totals with the <strong className="font-medium text-gray-600">Updates daily</strong> badge
                  come from a scheduled job; first snapshot is often within ~24 hours of a new environment, or run the
                  storage-gauges Lambda once from AWS for an immediate row.
                </>
              )}
              {range?.from && range?.to ? (
                <span className="text-gray-400">
                  {' '}
                  · {range.from} → {range.to}
                </span>
              ) : null}
            </p>
          </div>
        )}
        {suppressOuterTitle && range?.from && range?.to && (
          <p className="text-xs text-gray-400 sm:mr-auto sm:self-end">
            Range {range.from} → {range.to}
          </p>
        )}
        <div className="flex items-center gap-2">
          <label htmlFor={monthsFieldId} className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Range
          </label>
          <select
            id={monthsFieldId}
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
          >
            <option value={3}>Last 3 months</option>
            <option value={6}>Last 6 months</option>
            <option value={12}>Last 12 months</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}

      {payload?.customerEstimates && !suppressCustomerEstimates && (
        <div className="rounded-xl border border-emerald-100 bg-gradient-to-br from-emerald-50/90 to-teal-50/50 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-emerald-100/80 bg-white/60">
            <h4 className="text-sm font-semibold text-emerald-950">Estimated platform charges (USD)</h4>
            <p className="text-xs text-emerald-900/80 mt-0.5 leading-relaxed">
              {payload.customerEstimates.disclaimer ||
                'Indicative estimates only; not an invoice. Metering lag and rounding may differ from final amounts.'}
            </p>
          </div>
          <div className="overflow-x-auto bg-white/40">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-emerald-900/70 border-b border-emerald-100/80">
                  <th className="px-4 py-2.5">Line item</th>
                  <th className="px-4 py-2.5">Quantity (range)</th>
                  <th className="px-4 py-2.5 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(payload.customerEstimates.lines || []).map((row) => (
                  <tr key={row.id} className="border-b border-emerald-50/80 hover:bg-white/60">
                    <td className="px-4 py-2.5 font-medium text-gray-900">{row.label}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-600 tabular-nums">{row.quantityDisplay}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium text-gray-900">
                      {formatUsd(row.amountUsd)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-emerald-50/50">
                  <td colSpan={2} className="px-4 py-3 text-sm font-semibold text-emerald-950">
                    Total (range)
                  </td>
                  <td className="px-4 py-3 text-right text-base font-semibold tabular-nums text-emerald-950">
                    {formatUsd(payload.customerEstimates.totalUsd)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {(accountMode || orgMode) && userGlobalEntries.length > 0 && (
        <div className="rounded-xl border border-amber-100 bg-amber-50/40 px-4 py-3 shadow-sm">
          <h4 className="text-sm font-semibold text-gray-800">
            {orgMode ? 'Org-wide user-global infra' : 'Account-level infra (no network)'}
          </h4>
          <p className="text-xs text-gray-500 mt-0.5 mb-3">
            Included in the tab compute rollups below; shown separately for attribution (e.g. list-networks REST calls).
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-amber-100/80">
                  <th className="py-2 pr-4">Metric</th>
                  <th className="py-2 text-right">Total (range)</th>
                </tr>
              </thead>
              <tbody>
                {userGlobalEntries.map(([key, val]) => (
                  <tr key={key} className="border-b border-amber-50/80">
                    <td className="py-2 pr-4 font-mono text-xs text-gray-600">{key}</td>
                    <td className="py-2 text-right tabular-nums text-gray-900">{formatMetricNumber(key, val)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loading && !payload ? (
        <div className="flex justify-center py-20">
          <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
        {!hasAnyStorageGaugeInRange && monthRows.length > 0 && (
          <div className="rounded-lg border border-sky-100 bg-sky-50/70 px-4 py-3 text-sm text-sky-950">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className="font-medium">No storage snapshot in this date range yet.</span>
              <StorageGaugeDailyBadge />
            </div>
            The scheduled job writes gauge rows to your metering table (function name{' '}
            <code className="rounded bg-white/90 px-1 py-0.5 text-xs text-gray-800">
              stepflow-infra-storage-gauges-…
            </code>
            , typically once daily). Expect the first values within about a day of a fresh stack, or run that job once
            from your deployment console to populate immediately.{' '}
            <span className="text-sky-900/90">Compute rollups are separate and may already show usage.</span>
          </div>
        )}

        <Tab.Group>
          <Tab.List
            className={
              tabDefs.length <= 1
                ? 'sr-only'
                : 'flex flex-wrap gap-1.5 rounded-xl bg-slate-100/90 p-1.5'
            }
            aria-label={tabDefs.length <= 1 ? undefined : 'Platform billing categories'}
          >
            {tabData.map((tab) => {
              const Icon = tab.icon;
              return (
                <Tab
                  key={tab.id}
                  title={tab.tabTitle || tab.label}
                  className={({ selected }) =>
                    `flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all ${
                      selected
                        ? 'bg-white text-gray-900 shadow-md ring-1 ring-slate-200/80'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/60'
                    }`
                  }
                >
                  <Icon className="w-4 h-4 opacity-80 shrink-0" aria-hidden />
                  {tab.label}
                </Tab>
              );
            })}
          </Tab.List>

          <Tab.Panels className="mt-6">
            {tabData.map((tab) => {
              const hero = storageHeroLabels(tab.id);
              const cs = tab.computeSummary || { vpcuSeconds: 0, gbSeconds: 0, durationMs: 0 };
              const hasComputeSummary =
                cs.vpcuSeconds > 0 || cs.gbSeconds > 0 || cs.durationMs > 0 || tab.computeEntries.length > 0;
              return (
              <Tab.Panel key={tab.id}>
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-6"
                >
                  <div
                    className={`relative overflow-hidden rounded-2xl border border-slate-100 bg-gradient-to-br ${tab.accent} p-[1px]`}
                  >
                    <div className="rounded-2xl bg-white px-6 py-5">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                        <div>
                          <div className="flex flex-wrap items-center gap-2 gap-y-1">
                            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{hero.kicker}</p>
                            <StorageGaugeDailyBadge />
                          </div>
                          <p className="mt-0.5 text-xs text-gray-500 leading-snug max-w-xl">{hero.sub}</p>
                          <p className="mt-3 text-3xl font-semibold tracking-tight text-gray-900 tabular-nums">
                            {formatBytes(tab.latestBytes)}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            {tab.latestCollectedAt
                              ? `Last scan ${new Date(tab.latestCollectedAt).toLocaleString()}`
                              : 'No snapshot in this range yet — daily collector (~24h on new env) or run stepflow-infra-storage-gauges once; widen the month range if needed.'}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 text-gray-400">
                          <Cpu className="w-10 h-10 opacity-30" strokeWidth={1.25} aria-hidden />
                        </div>
                      </div>
                    </div>
                  </div>

                  {tab.id === 'vectorDb' && (
                    <div className="rounded-xl border border-violet-100 bg-gradient-to-br from-violet-50/90 to-indigo-50/40 px-4 py-3.5 shadow-sm">
                      <p className="text-sm font-semibold text-violet-950">Why this tab matters</p>
                      <ul className="mt-2 space-y-1.5 text-xs text-violet-900/90 leading-relaxed list-disc pl-4">
                        <li>
                          <span className="font-medium text-violet-950">Capacity</span> — see how much vector index data
                          is attributed to this network (storage ROI).
                        </li>
                        <li>
                          <span className="font-medium text-violet-950">Workload</span> — ingest, sync, and search
                          compute in one view (fair attribution vs. rolling your own).
                        </li>
                        <li>
                          <span className="font-medium text-violet-950">Value</span> — defend spend and adoption with the
                          same numbers we use for metering—no extra observability stack required.
                        </li>
                      </ul>
                    </div>
                  )}

                  {(tab.id === 'vectorDb' || tab.id === 'general') && (
                    <div className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-100 bg-slate-50/80">
                        <h4 className="text-sm font-semibold text-gray-800">
                          {tab.id === 'vectorDb'
                            ? 'Managed engine compute · this range'
                            : 'Platform compute (this range)'}
                        </h4>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {tab.id === 'vectorDb'
                            ? 'Rolls up only vector-stream, memory-search, and cold/hot memory pipelines—the work you’d otherwise run on self-hosted vector workers. vCPU-seconds and GB-seconds are capacity-time units; wall time is recorded duration.'
                            : 'REST, workflows, doc processing, and related workers. Same units where we emit them from metering.'}
                        </p>
                        <p className="text-[11px] text-gray-500 mt-1.5">
                          <span className="font-medium text-gray-600">Compute rollups</span> update as usage is recorded
                          (typically within minutes)—not on the same daily cadence as storage byte snapshots.
                        </p>
                      </div>
                      {hasComputeSummary ? (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 p-5">
                          <div className="rounded-lg border border-gray-50 bg-slate-50/40 px-4 py-3">
                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">vCPU-seconds</p>
                            <p className="mt-1 text-xl font-semibold tabular-nums text-gray-900">
                              {cs.vpcuSeconds > 0
                                ? formatMetricNumber('vectorStreamProcessorVpcuSeconds', cs.vpcuSeconds)
                                : '—'}
                            </p>
                            <p className="text-[11px] text-gray-400 mt-1">Effective CPU-time across vector workloads</p>
                          </div>
                          <div className="rounded-lg border border-gray-50 bg-slate-50/40 px-4 py-3">
                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">GB-seconds</p>
                            <p className="mt-1 text-xl font-semibold tabular-nums text-gray-900">
                              {cs.gbSeconds > 0
                                ? formatMetricNumber('vectorStreamProcessorGbSeconds', cs.gbSeconds)
                                : '—'}
                            </p>
                            <p className="text-[11px] text-gray-400 mt-1">Memory × time (capacity units)</p>
                          </div>
                          <div className="rounded-lg border border-gray-50 bg-slate-50/40 px-4 py-3">
                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Wall time</p>
                            <p className="mt-1 text-xl font-semibold tabular-nums text-gray-900">
                              {formatDurationMsTotal(cs.durationMs)}
                            </p>
                            <p className="text-[11px] text-gray-400 mt-1">Sum of recorded durations in this tab</p>
                          </div>
                        </div>
                      ) : (
                        <p className="px-4 py-6 text-sm text-gray-500 text-center">
                          {tab.id === 'vectorDb'
                            ? 'No vector workload yet in this window—as soon as ingest, sync, or memory-search runs, you’ll see compute here and can tie it to business usage.'
                            : 'No compute rollups in this range yet — usage appears as REST, workflows, and doc jobs run.'}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 bg-slate-50/80">
                      <div className="flex flex-wrap items-center gap-2 gap-y-1">
                        <h4 className="text-sm font-semibold text-gray-800">
                          {tab.id === 'vectorDb' ? 'Index storage by month' : 'Storage by month'}
                        </h4>
                        <StorageGaugeDailyBadge />
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {tab.id === 'vectorDb'
                          ? accountMode || orgMode
                            ? 'Per month: combined vector-index bytes across your networks (each cell is a snapshot, not cumulative across months).'
                            : 'How your managed vector index footprint changed month to month (scheduled daily job).'
                          : accountMode || orgMode
                            ? 'Per month: sum of all networks’ gauge snapshots (not additive across months).'
                            : 'Point-in-time totals from metering (not additive).'}
                      </p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100">
                            <th className="px-4 py-3">Month</th>
                            <th className="px-4 py-3 text-right">Storage</th>
                            <th className="px-4 py-3 text-right hidden sm:table-cell">Collected</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tab.storageByMonth.map((row) => (
                            <tr key={row.month} className="border-b border-gray-50 hover:bg-slate-50/50">
                              <td className="px-4 py-3 font-medium text-gray-900">{row.month}</td>
                              <td className="px-4 py-3 text-right tabular-nums text-gray-800">
                                {row.bytes != null ? formatBytes(row.bytes) : '—'}
                              </td>
                              <td className="px-4 py-3 text-right text-xs text-gray-500 hidden sm:table-cell">
                                {row.collectedAt ? new Date(row.collectedAt).toLocaleDateString() : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {!tab.hasGaugeRun && (
                      <p className="px-4 py-4 text-sm text-gray-600 border-t border-gray-50 bg-amber-50/40">
                        No gauge rows with a collection timestamp in this range. Confirm the meter stack is deployed and
                        the daily schedule is enabled, run{' '}
                        <code className="text-xs bg-white/80 px-1 rounded">stepflow-infra-storage-gauges-…</code> once from
                        your operator console if you need data immediately, or widen the month range.
                      </p>
                    )}
                  </div>

                  <div className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 bg-slate-50/80">
                      <h4 className="text-sm font-semibold text-gray-800">Metric breakdown</h4>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {tab.id === 'buffer'
                          ? 'Buffer is storage-only for now; no separate compute keys in this category.'
                          : tab.id === 'vectorDb'
                            ? 'Line-item counters (records processed, batches, invocations, per-stage ms, etc.). The managed summary above rolls vCPU-seconds, GB-seconds, and wall time for executive-style ROI.'
                            : 'Every counter in this tab for the selected range. Rolled-up vCPU-seconds, GB-seconds, and wall time are summarized above.'}
                      </p>
                    </div>
                    {tab.computeEntries.length === 0 ? (
                      <div className="py-12 text-center text-sm text-gray-500 border-t border-gray-50">
                        {tab.id === 'buffer'
                          ? 'Buffer tab tracks DynamoDB payload size above — no separate compute keys in this category yet.'
                          : 'No matching metrics in this period yet.'}
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100">
                              <th className="px-4 py-3">Metric</th>
                              <th className="px-4 py-3 text-right">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tab.computeEntries.map(([key, val]) => (
                              <tr key={key} className="border-b border-gray-50 hover:bg-slate-50/50">
                                <td className="px-4 py-3">
                                  <span className="font-medium text-gray-900">{humanizeMetricKey(key)}</span>
                                  <div className="text-[11px] text-gray-400 font-mono truncate max-w-md" title={key}>
                                    {key}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums text-gray-800 font-medium">
                                  {formatMetricNumber(key, val)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </motion.div>
              </Tab.Panel>
              );
            })}
          </Tab.Panels>
        </Tab.Group>

        {(accountMode || orgMode) && byNetwork.length > 0 && (
          <div className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-slate-50/80">
              <div className="flex flex-wrap items-center gap-2 gap-y-1">
                <h4 className="text-sm font-semibold text-gray-800">By network</h4>
                <StorageGaugeDailyBadge />
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Storage columns use the latest daily snapshot in this range per network. Row-level compute totals sum
                infra rollups over the range (not daily).
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100">
                    <th className="px-4 py-3">Network</th>
                    <th className="px-4 py-3 text-right">Vector (managed)</th>
                    <th className="px-4 py-3 text-right">General (app S3)</th>
                    <th className="px-4 py-3 text-right">Buffer</th>
                    <th className="px-4 py-3 text-right w-24" />
                  </tr>
                </thead>
                <tbody>
                  {byNetwork.map((row) => {
                    const g = row.storageGauges;
                    return (
                      <tr key={row.networkId} className="border-b border-gray-50 hover:bg-slate-50/50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{row.name || row.networkId}</div>
                          <div className="text-[11px] text-gray-400 font-mono truncate max-w-[200px]">{row.networkId}</div>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-800">
                          {formatBytes(g?.s3LanceBytesReported)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-800">
                          {formatBytes(g?.s3AppDataBytesReported)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-800">
                          {formatBytes(g?.dynamoBufferBytesReported)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Link
                            to={`/network/${row.networkId}`}
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            Open
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>
      )}
    </div>
  );
}
